import { useCallback } from 'react';
import type { RefObject } from 'react';
import type { FormikHelpers } from 'formik';

import { ensureDefined } from '@e-socna/utils';
import { EMPTY_OBJECT } from '@e-socna/empty';

import { executeFormikOnNextTick } from '../../../hooks/useFormikOnNextTick.hook';

import { useWizardContext } from '../contexts/WizardContext/hooks/useWizardContext.hook';

import { useSteps } from './useSteps.hook';

export const useActions = <V extends object, VS extends object, SE>({
  formRef,
}: Readonly<{
  formRef: RefObject<HTMLFormElement | null>;
}>) => {
  const {
    memos,
    props,
    setCurrentStepId,
    setMemos,
    setSnapshot,
    setWizardError,
  } = useWizardContext<V, VS, SE>();

  const { onSubmit: pHandleSubmit } = props;

  const {
    currentEnabledIndexInEnabledSteps,
    currentEnabledStepCmpProps,
    enabledSteps,
    isLastEnabledStep,
  } = useSteps<V, VS, SE>();

  const handleSubmit = useCallback(
    (values: V, formikHelpers: FormikHelpers<V>) => {
      const next = (values: V) => {
        const { nextStepId } = ensureDefined<{ nextStepId: SE }>({
          nextStepId: enabledSteps[currentEnabledIndexInEnabledSteps + 1]?.id,
        });

        setSnapshot(values);
        setCurrentStepId(nextStepId);

        window.scrollTo(0, 0);
      };

      const isValid = formRef.current?.checkValidity();

      if (!isValid) {
        formikHelpers.setSubmitting(false);
        formRef.current?.reportValidity();
      } else {
        if (currentEnabledStepCmpProps?.onSubmit) {
          void currentEnabledStepCmpProps.onSubmit(values, formikHelpers);
        }

        if (isLastEnabledStep) {
          void pHandleSubmit({ values, memos, formikHelpers, setWizardError });
        } else {
          void formikHelpers.setTouched(EMPTY_OBJECT);

          next(values);

          if (!currentEnabledStepCmpProps?.onSubmit) {
            formikHelpers.setSubmitting(false);
          }
        }
      }
    },
    [
      currentEnabledIndexInEnabledSteps,
      currentEnabledStepCmpProps,
      enabledSteps,
      formRef,
      isLastEnabledStep,
      memos,
      pHandleSubmit,
      setCurrentStepId,
      setSnapshot,
      setWizardError,
    ],
  );

  const previous = useCallback(
    (values: V, formikHelpers: FormikHelpers<V>) => {
      const { previousStepId } = ensureDefined<{ previousStepId: SE }>({
        previousStepId: enabledSteps[currentEnabledIndexInEnabledSteps - 1]?.id,
      });

      setSnapshot(values);
      setCurrentStepId(previousStepId);

      // don't lock user if he goes back from a failed step
      // we need to reset errors and touched, then revalidate on next tick
      // to ensure the validation runs against the previous step schema
      executeFormikOnNextTick(formikHelpers, (helpers) => {
        void helpers.setTouched(EMPTY_OBJECT);
        helpers.setErrors(EMPTY_OBJECT);
        void helpers.validateForm();
      });

      window.scrollTo(0, 0);
    },
    [
      currentEnabledIndexInEnabledSteps,
      enabledSteps,
      setSnapshot,
      setCurrentStepId,
    ],
  );

  const showMemoDialog = useCallback(() => {
    const isValid = formRef.current?.checkValidity();

    if (isValid) {
      setMemos((oldMemos) => ({
        ...oldMemos,
        isOpenMemoDialog: true,
      }));
    } else {
      formRef.current?.reportValidity();
    }
  }, [formRef, setMemos]);

  return { handleSubmit, previous, showMemoDialog };
};
